/**
 * Finance — Gemini assistant + Pricing / COGS (Cosmos-backed via finance-service).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Textarea,
  Spinner,
  Card,
  CardHeader,
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
  MessageBar,
  MessageBarBody,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Input,
  Badge,
} from "@fluentui/react-components";
import {
  Send24Regular,
  Chat24Regular,
  Money24Regular,
  DocumentTable24Regular,
  DocumentBulletList24Regular,
} from "@fluentui/react-icons";

import useFinanceApiClient from "../../hooks/useFinanceApiClient";
import { VoiceTextInputButton } from "../../components/VoiceTextInputButton";
import {
  ProposalList,
  ProposalWizard,
  ProposalPreview,
  ProposalDetail,
} from "./proposals";
import type { Proposal } from "./proposals";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerTitle: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  headerIcon: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorPaletteGreenBackground3,
    borderRadius: "6px",
    color: tokens.colorNeutralForegroundOnBrand,
  },
  tabList: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  content: { flex: 1, display: "flex", overflow: "hidden", minHeight: 0 },
  chatMain: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  messages: { flex: 1, overflowY: "auto", padding: tokens.spacingVerticalL, paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL },
  message: { marginBottom: tokens.spacingVerticalM, maxWidth: "800px" },
  messageUser: { marginLeft: "auto" },
  bubbleUser: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderRadius: "12px",
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorNeutralForeground1,
  },
  bubbleAi: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderRadius: "12px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  composer: {
    padding: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  notConfigured: { display: "flex", alignItems: "center", justifyContent: "center", flex: 1, padding: tokens.spacingVerticalXXL },
  pricingPane: { flex: 1, overflow: "auto", padding: tokens.spacingVerticalL },
  pricingTable: { tableLayout: "fixed", width: "100%" },
  /** ~2× prior default name share — room for long product titles */
  colName: { width: "40%", minWidth: "20rem" },
  colPart: { width: "10%", maxWidth: "10rem" },
  colCategory: { width: "14%" },
  /** Fits up to 8 digits + decimal (e.g. 123456.78) */
  colMoney: { width: "9ch", maxWidth: "9ch", minWidth: "9ch", boxSizing: "border-box" },
  moneyInput: { width: "9ch", maxWidth: "9ch", minWidth: "9ch", boxSizing: "border-box" },
});

type TabValue = "chat" | "pricing" | "proposals";

type ProposalView =
  | { kind: "list" }
  | { kind: "wizard"; existing?: Proposal }
  | { kind: "preview"; proposal: Proposal }
  | { kind: "detail"; proposal: Proposal };

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface FinancePricingLine {
  id: string;
  name: string;
  /** Optional SKU / MPN; column shown blank until populated */
  part_number?: string;
  category?: string;
  price: { amount: number | null; status: string };
  cogs: { amount: number | null; status: string };
  notes?: string;
  sort_order?: number;
}

interface FinancePermissions {
  can_read: boolean;
  can_write: boolean;
}

export const Finance: React.FC = () => {
  const styles = useStyles();
  const financeApi = useFinanceApiClient();
  const [tab, setTab] = useState<TabValue>("chat");
  const [perms, setPerms] = useState<FinancePermissions | null>(null);
  const [lines, setLines] = useState<FinancePricingLine[]>([]);
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [loadingLines, setLoadingLines] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamText, setStreamText] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [proposalView, setProposalView] = useState<ProposalView>({ kind: "list" });

  const configured = financeApi.isConfigured();

  const loadPerms = useCallback(async () => {
    if (!configured) return;
    setLoadingPerms(true);
    try {
      const data = await financeApi.get<{ can_read: boolean; can_write: boolean }>("/api/permissions");
      setPerms({ can_read: data.can_read, can_write: data.can_write });
    } catch (e) {
      console.error(e);
      setPerms({ can_read: false, can_write: false });
      setError(e instanceof Error ? e.message : "Failed to load permissions");
    } finally {
      setLoadingPerms(false);
    }
  }, [configured, financeApi]);

  const loadLines = useCallback(async () => {
    if (!configured || !perms?.can_read) return;
    setLoadingLines(true);
    setError(null);
    try {
      const data = await financeApi.get<{ lines: FinancePricingLine[] }>("/api/pricing/lines");
      setLines(data.lines || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load pricing");
    } finally {
      setLoadingLines(false);
    }
  }, [configured, financeApi, perms?.can_read]);

  useEffect(() => {
    loadPerms();
  }, [loadPerms]);

  useEffect(() => {
    if (tab === "pricing" && perms?.can_read) {
      loadLines();
    }
  }, [tab, perms?.can_read, loadLines]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamText]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !perms?.can_read) return;
    setInput("");
    setSending(true);
    setStreamText("");
    setError(null);
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: text }]);

    try {
      let headers = await financeApi.getAuthHeaders();
      let res = await fetch(`${financeApi.getBaseUrl()}/api/chat/send/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({ message: text, history }),
      });
      if (res.status === 401) {
        headers = await financeApi.getAuthHeaders(true);
        res = await fetch(`${financeApi.getBaseUrl()}/api/chat/send/stream`, {
          method: "POST",
          headers,
          body: JSON.stringify({ message: text, history }),
        });
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        buf += decoder.decode(value ?? new Uint8Array(), { stream: !done });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          let data: { type?: string; content?: string };
          try {
            data = JSON.parse(raw);
          } catch {
            continue;
          }
          if (data.type === "text" && data.content) {
            full += data.content;
            setStreamText(full);
          } else if (data.type === "error") {
            throw new Error(data.content || "Error");
          }
        }
        if (done) break;
      }
      setMessages((prev) => [...prev, { role: "assistant", content: full || "(No response)" }]);
      setStreamText("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Send failed");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const saveLine = async (line: FinancePricingLine, patch: Partial<FinancePricingLine>) => {
    if (!perms?.can_write) return;
    try {
      await financeApi.put(`/api/pricing/lines/${encodeURIComponent(line.id)}`, patch);
      await loadLines();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  };

  if (!configured) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <div className={styles.headerIcon}><Money24Regular /></div>
            <div>
              <Text size={500} weight="semibold">Finance</Text>
              <Text size={200} style={{ display: "block", color: tokens.colorNeutralForeground3 }}>Assistant and pricing</Text>
            </div>
          </div>
        </div>
        <div className={styles.notConfigured}>
          <Card style={{ maxWidth: 520 }}>
            <CardHeader header={<Text weight="semibold">Finance service not configured</Text>} />
            <Text size={200} style={{ padding: tokens.spacingHorizontalM }}>
              Set <code>financeServiceUrl</code> in <code>config.json</code> (or <code>REACT_APP_FINANCE_SERVICE_URL</code>) to the finance-service Container App URL.
            </Text>
          </Card>
        </div>
      </div>
    );
  }

  if (loadingPerms) {
    return (
      <div className={styles.container} style={{ alignItems: "center", justifyContent: "center" }}>
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (!perms?.can_read) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <div className={styles.headerIcon}><Money24Regular /></div>
            <Text size={500} weight="semibold">Finance</Text>
          </div>
        </div>
        <MessageBar intent="warning" style={{ margin: tokens.spacingHorizontalL }}>
          <MessageBarBody>You do not have Finance User access. Ask a portal admin to grant Finance User or Finance Admin in Admin → Permissions.</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <div className={styles.headerIcon}><Money24Regular /></div>
          <div>
            <Text size={500} weight="semibold">Finance</Text>
            <Text size={200} style={{ display: "block", color: tokens.colorNeutralForeground3 }}>
              Assistant and pricing / COGS
            </Text>
          </div>
        </div>
      </div>

      <div className={styles.tabList}>
        <TabList
          selectedValue={tab}
          onTabSelect={(_e: SelectTabEvent, d: SelectTabData) => setTab(d.value as TabValue)}
        >
          <Tab value="chat" icon={<Chat24Regular />}>Assistant</Tab>
          <Tab value="pricing" icon={<DocumentTable24Regular />}>Pricing and COGS</Tab>
          <Tab value="proposals" icon={<DocumentBulletList24Regular />}>Proposals</Tab>
        </TabList>
      </div>

      <div className={styles.content}>
        {error && (
          <MessageBar intent="error" style={{ margin: tokens.spacingHorizontalM }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {tab === "chat" && (
          <div className={styles.chatMain}>
            <div className={styles.messages}>
              {messages.map((m, i) => (
                <div key={i} className={styles.message + (m.role === "user" ? ` ${styles.messageUser}` : "")}>
                  {m.role === "user" ? (
                    <div className={styles.bubbleUser}>{m.content}</div>
                  ) : (
                    <div className={styles.bubbleAi}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              ))}
              {streamText && (
                <div className={styles.message}>
                  <div className={styles.bubbleAi}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamText}</ReactMarkdown>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className={styles.composer}>
              <Textarea
                style={{ flex: 1 }}
                value={input}
                onChange={(_, e) => setInput(e.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Ask about finance topics…"
                resize="vertical"
                rows={2}
              />
              <VoiceTextInputButton value={input} onChange={setInput} disabled={sending} />
              <Button appearance="primary" icon={<Send24Regular />} onClick={() => sendMessage()} disabled={sending}>
                Send
              </Button>
            </div>
          </div>
        )}

        {tab === "pricing" && (
          <div className={styles.pricingPane}>
            {loadingLines ? (
              <Spinner label="Loading pricing…" />
            ) : (
              <Table className={styles.pricingTable}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className={styles.colName}>Name</TableHeaderCell>
                    <TableHeaderCell className={styles.colPart}>Part number</TableHeaderCell>
                    <TableHeaderCell className={styles.colCategory}>Category</TableHeaderCell>
                    <TableHeaderCell className={styles.colMoney}>List Price</TableHeaderCell>
                    <TableHeaderCell className={styles.colMoney}>COGS</TableHeaderCell>
                    {perms.can_write && <TableHeaderCell>Notes</TableHeaderCell>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => (
                    <PricingRow
                      key={line.id}
                      line={line}
                      canWrite={perms.can_write}
                      onSave={saveLine}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {tab === "proposals" && (
          <div style={{ flex: 1, overflow: "hidden" }}>
            {proposalView.kind === "list" && (
              <ProposalList
                isAdmin={perms.can_write}
                onNewProposal={() => setProposalView({ kind: "wizard" })}
                onSelectProposal={(p) => {
                  if (p.status === "draft" || p.status === "changes_requested") {
                    setProposalView({ kind: "preview", proposal: p });
                  } else {
                    setProposalView({ kind: "detail", proposal: p });
                  }
                }}
              />
            )}
            {proposalView.kind === "wizard" && (
              <ProposalWizard
                existingProposal={proposalView.existing}
                onBack={() => setProposalView({ kind: "list" })}
                onSaved={(p) => setProposalView({ kind: "preview", proposal: p })}
              />
            )}
            {proposalView.kind === "preview" && (
              <ProposalPreview
                proposal={proposalView.proposal}
                onBack={() => setProposalView({ kind: "list" })}
                onEdit={() =>
                  setProposalView({ kind: "wizard", existing: proposalView.proposal })
                }
                onSubmitted={(p) => setProposalView({ kind: "detail", proposal: p })}
                onRefresh={(p) => setProposalView({ kind: "preview", proposal: p })}
              />
            )}
            {proposalView.kind === "detail" && (
              <ProposalDetail
                proposal={proposalView.proposal}
                isAdmin={perms.can_write}
                onBack={() => setProposalView({ kind: "list" })}
                onRefresh={(p) => setProposalView({ kind: "detail", proposal: p })}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PricingRow: React.FC<{
  line: FinancePricingLine;
  canWrite: boolean;
  onSave: (line: FinancePricingLine, patch: Partial<FinancePricingLine>) => void;
}> = ({ line, canWrite, onSave }) => {
  const styles = useStyles();
  const [priceIn, setPriceIn] = useState(
    line.price?.status === "SET" && line.price.amount != null ? String(line.price.amount) : "",
  );
  const [cogsIn, setCogsIn] = useState(
    line.cogs?.status === "SET" && line.cogs.amount != null ? String(line.cogs.amount) : "",
  );
  const [notesIn, setNotesIn] = useState(line.notes || "");

  useEffect(() => {
    setPriceIn(line.price?.status === "SET" && line.price.amount != null ? String(line.price.amount) : "");
    setCogsIn(line.cogs?.status === "SET" && line.cogs.amount != null ? String(line.cogs.amount) : "");
    setNotesIn(line.notes || "");
  }, [line]);

  const fmtMoney = (p: { amount: number | null; status: string }) => {
    if (p.status === "TBD" || p.amount == null) return <Badge appearance="tint">TBD</Badge>;
    return <Text>{p.amount}</Text>;
  };

  if (!canWrite) {
    return (
      <TableRow>
        <TableCell className={styles.colName}>{line.name}</TableCell>
        <TableCell className={styles.colPart} />
        <TableCell className={styles.colCategory}>{line.category || "—"}</TableCell>
        <TableCell className={styles.colMoney}>{fmtMoney(line.price)}</TableCell>
        <TableCell className={styles.colMoney}>{fmtMoney(line.cogs)}</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className={styles.colName}>
        <Input
          defaultValue={line.name}
          key={`n-${line.id}-${line.name}`}
          onBlur={(e) => onSave(line, { name: e.target.value })}
        />
      </TableCell>
      <TableCell className={styles.colPart} />
      <TableCell className={styles.colCategory}>
        <Input
          defaultValue={line.category || ""}
          key={`c-${line.id}-${line.category}`}
          onBlur={(e) => onSave(line, { category: e.target.value })}
        />
      </TableCell>
      <TableCell className={styles.colMoney}>
        <Input
          className={styles.moneyInput}
          type="number"
          placeholder="TBD"
          value={priceIn}
          onChange={(_, e) => setPriceIn(e.value)}
          onBlur={() => {
            const v = priceIn.trim();
            if (!v) {
              onSave(line, { price: { amount: null, status: "TBD" } });
            } else {
              const n = parseFloat(v);
              if (!Number.isNaN(n)) {
                onSave(line, { price: { amount: n, status: "SET" } });
              }
            }
          }}
        />
      </TableCell>
      <TableCell className={styles.colMoney}>
        <Input
          className={styles.moneyInput}
          type="number"
          placeholder="TBD"
          value={cogsIn}
          onChange={(_, e) => setCogsIn(e.value)}
          onBlur={() => {
            const v = cogsIn.trim();
            if (!v) {
              onSave(line, { cogs: { amount: null, status: "TBD" } });
            } else {
              const n = parseFloat(v);
              if (!Number.isNaN(n)) {
                onSave(line, { cogs: { amount: n, status: "SET" } });
              }
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Input
          value={notesIn}
          onChange={(_, e) => setNotesIn(e.value)}
          onBlur={() => onSave(line, { notes: notesIn })}
        />
      </TableCell>
    </TableRow>
  );
};

export default Finance;
